import React, { useMemo } from 'react';
import RcTooltip from 'rc-tooltip';
import axios from 'axios';
import { Button, CircularProgress, Popover, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  MERGED_BREAK_TIME_REGEX,
} from '@src/constants/voice';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import { countTextLength } from '@src/services/tts';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useTranslation } from 'react-i18next';
import { updateProject } from '@src/apis/project';
import { AUDIO_MODE, CONVERTING_STATUS } from '@src/constants/project';
import apis from '@src/apis';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { downloadFileByUrl, getFileName } from '@src/utils/file';
import { audioExpired, isBlockChanged } from '@src/services/project';
import {
  PopoverButton,
  StyledDownloadMenu,
  PopoverMenuItem,
} from './index.style';

const StructureOptions = [
  { label: 'singleFile', value: 'singleFile' },
  { label: 'paragraphsZip', value: 'paragraphsZip' },
];

const FormatOptions = [{ label: 'MP3', value: 'mp3' }];

const OverLayComponent = ({
  handleDownload,
  onChangeStructure,
  currentStructure,
  showLoadingInTooltip,
}) => {
  const { t } = useTranslation();
  const [anchorStructure, setAnchorStructure] = React.useState(null);

  const handleClickStructure = (e) => {
    setAnchorStructure(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorStructure(null);
  };

  const renderArrow = (anchor) => {
    if (anchor) {
      return <ArrowDropUpRoundedIcon width={24} height={24} />;
    }
    return <ArrowDropDownRoundedIcon width={24} height={24} />;
  };

  return (
    <StyledDownloadMenu>
      <div className="row">
        <div className="label">{t('type')}</div>
        <PopoverButton active={!!anchorStructure}>
          <Button className="custom-button" onClick={handleClickStructure}>
            <span className="title-button">{t(currentStructure?.label)}</span>
            {renderArrow(anchorStructure)}
          </Button>
          <Popover
            open={Boolean(anchorStructure)}
            anchorEl={anchorStructure}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            className="popover-structure"
            PaperProps={{
              style: { borderRadius: '8px' },
            }}
          >
            <div
              style={{
                padding: '8px 0px',
                border: '1px solid #1E164E52',
                borderRadius: '8px',
              }}
            >
              {StructureOptions.map((item) => (
                <PopoverMenuItem
                  onClick={() => {
                    onChangeStructure(item);
                    setAnchorStructure(null);
                  }}
                  active={currentStructure?.value === item?.value}
                  key={item.label}
                >
                  <div className="wrap-label">
                    <Typography variant="body2" className="text">
                      {t(item.label)}
                    </Typography>
                    {currentStructure?.value === item?.value && (
                      <CheckRoundedIcon sx={{ color: COLOR.darkBlue }} />
                    )}
                  </div>
                </PopoverMenuItem>
              ))}
            </div>
          </Popover>
        </PopoverButton>
      </div>

      <Button
        className="download-btn"
        variant="contained"
        onClick={() => handleDownload()}
        disabled={showLoadingInTooltip}
      >
        {showLoadingInTooltip ? (
          <CircularProgress
            value={75}
            className="circular-progress"
            size="24px"
          />
        ) : (
          t('download')
        )}
      </Button>
    </StyledDownloadMenu>
  );
};

const DownloadButton = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { multiBlock, title, isDownloading, voiceDefault, audioType } =
    useSelector((state) => state.project);
  const location = useLocation();
  const projectId = location.pathname.split('/')[2];
  const [currentFormat, setCurrentFormat] = React.useState(FormatOptions[0]);
  const [currentStructure, setCurrentStructure] = React.useState(
    StructureOptions[0],
  );
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);

  const handleTooltipVisibleChange = (visible) => {
    setIsTooltipOpen(visible);
  };

  const { blocks, originalBlocks } = multiBlock || {};
  const blockWithsText = Object.values(blocks).filter((block) => block?.text);

  const saveProject = async () => {
    const response = await updateProject(projectId, {
      title,
      blocks: multiBlock?.blockIds?.map((blockId) => ({
        id: blockId,
        characters: countTextLength(blocks[blockId].text || ''),
        text: blocks[blockId].text || undefined,
        voiceCode: blocks[blockId]?.voiceCode || voiceDefault?.code,
        breakTime: blocks[blockId]?.breakTime || DEFAULT_BREAK_TIME,
        speed: blocks[blockId]?.speed || DEFAULT_SPEED,
        elements: blocks[blockId]?.elements,
        isSample: blocks[blockId]?.isSample || false,
      })),
      audioType,
    });
    return response;
  };

  const hasAudioExpired = blockWithsText.some((block) =>
    audioExpired(block?.audioExpiredAt),
  );
  const blockWithTextChanged = useMemo(
    () =>
      blockWithsText.filter((block) => {
        const withoutExtension = block?.audioLink?.split('.')?.pop();
        const currentBlock = {
          text: block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          audioType: withoutExtension,
        };
        const originalBlock = {
          text: originalBlocks[block.id]?.text,
          voiceCode: originalBlocks[block.id]?.voiceCode,
          speed: originalBlocks[block.id]?.speed,
          audioType,
        };
        return isBlockChanged(currentBlock, originalBlock) || !block?.audioLink;
      }),
    [blockWithsText, originalBlocks],
  );
  const isSynthesisInProgress =
    blockWithsText.filter(
      (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
    ).length > 0;

  const isDisabledBtnDownload =
    !blockWithsText?.length ||
    hasAudioExpired ||
    blockWithTextChanged?.length ||
    isSynthesisInProgress;
  const handleDownloadParagraphsZip = async () => {
    const audioLinks = blockWithsText.map((block) => ({
      audioLink: block.audioLink,
      title: block.text.substring(0, 30),
    }));
    const zip = new JSZip();
    const nameFile = `Vbee_${title}.zip`;
    const folder = zip.folder(`Vbee_${title}`);
    dispatch(actions.project.setIsDownloading(true));
    await Promise.all(
      audioLinks.map(async (url, i) => {
        const response = await axios.get(url.audioLink, {
          responseType: 'blob',
        });
        const blob = response.data;
        const filename = `${String(i + 1).padStart(2, '0')}_${url.title
          ?.trim()
          .replace(MERGED_BREAK_TIME_REGEX, '')}.${audioType}`;
        folder.file(filename, blob);
      }),
    );
    zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
      saveAs(zipBlob, nameFile);
    });
    dispatch(actions.project.setIsDownloading(false));
    setIsTooltipOpen(false);
  };

  const handleDownloadSingleFile = async () => {
    try {
      dispatch(actions.project.setIsDownloading(true));
      await saveProject();
      dispatch(actions.project.updateOriginalBlocks());
      const response = await apis.project.getJoinedAudio(
        projectId,
        AUDIO_MODE.JOINED,
      );
      const audioJoined = response?.result?.audioLink;
      if (response.status && audioJoined) {
        const downloadAudioLink = audioJoined;
        const downloadAudioName = getFileName(audioJoined);
        downloadFileByUrl(downloadAudioLink, downloadAudioName);
      }
    } finally {
      dispatch(actions.project.setIsDownloading(false));
      setIsTooltipOpen(false);
    }
  };

  const handleDownload = () => {
    const options = {
      singleFile: handleDownloadSingleFile,
      paragraphsZip: handleDownloadParagraphsZip,
    };

    return options[currentStructure?.value]();
  };

  const showLoadingButton = isDownloading && !isTooltipOpen;
  const showLoadingInTooltip = isDownloading && isTooltipOpen;

  return (
    <RcTooltip
      placement="bottomRight"
      overlay={
        <OverLayComponent
          onChangeStructure={setCurrentStructure}
          onChangeFormat={setCurrentFormat}
          handleDownload={handleDownload}
          currentStructure={currentStructure}
          currentFormat={currentFormat}
          showLoadingInTooltip={showLoadingInTooltip}
        />
      }
      trigger={['click']}
      showArrow={false}
      align={{
        offset: [0, 5],
      }}
      onVisibleChange={handleTooltipVisibleChange}
      visible={isTooltipOpen}
    >
      <Button
        startIcon={
          !showLoadingButton && (
            <DownloadRoundedIcon style={{ fontSize: '24px' }} />
          )
        }
        className="download-btn"
        variant="contained"
        disabled={isDisabledBtnDownload}
        onClick={() => setIsTooltipOpen(!isTooltipOpen)}
      >
        {showLoadingButton ? (
          <CircularProgress
            value={75}
            className="circular-progress"
            size="24px"
          />
        ) : (
          t('download')
        )}
      </Button>
    </RcTooltip>
  );
};

export default DownloadButton;
